import { supabase } from '@/lib/supabase';
import type {
  EvidenceStage,
  MyNotificationRow,
  PractitionerDashboardRow,
  QiCase,
  QiCaseForm,
  QiConsent,
  QiFollowup,
  QiMedia,
  QiNotification,
  QiPractitionerProfile,
  QiReview,
  QiRevision,
  QiSession,
  QiSessionRecord,
  RecipientConsentInput,
} from './types';

function normalizeSessionDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) throw new Error('施炁日期與時間格式錯誤。');
  return parsed.toISOString();
}

export function getQiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  if (error && typeof error === 'object') {
    const value = error as Record<string, unknown>;
    const parts = [value.message, value.details, value.hint, value.code]
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    if (parts.length > 0) return parts.join(' | ');
  }
  return fallback;
}

// ── Queries ─────────────────────────────────────────

export async function fetchMyCases(userId: string): Promise<QiCase[]> {
  const { data, error } = await supabase
    .from('qi_cases')
    .select('*')
    .or(`case_owner_id.eq.${userId},practitioner_id.eq.${userId},recipient_id.eq.${userId}`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as QiCase[]) || [];
}

export async function fetchAllCases(): Promise<QiCase[]> {
  const { data, error } = await supabase
    .from('qi_cases')
    .select('*, qi_sessions(id, session_number, session_datetime, location)')
    .order('created_at', { ascending: false });
  if (error) throw error;

  type CaseWithSessions = QiCase & {
    qi_sessions?: Array<{
      id: number;
      session_number: number;
      session_datetime: string | null;
      location: string | null;
    }>;
  };

  return ((data as CaseWithSessions[]) || []).map((row) => {
    const firstSession = [...(row.qi_sessions || [])]
      .filter((session) => session.session_number === 1)
      .sort((a, b) => a.id - b.id)[0];
    const { qi_sessions: _sessions, ...qiCase } = row;
    if (!firstSession?.session_datetime) return qiCase as QiCase;

    const dateParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(firstSession.session_datetime));
    const part = (type: 'year' | 'month' | 'day') =>
      dateParts.find((item) => item.type === type)?.value || '';
    const caseDate = `${part('year')}${part('month')}${part('day')}`;
    const correctedCode = qiCase.case_code?.match(/^UQ-[0-9]{8}-([0-9]{4})$/)
      ? qiCase.case_code.replace(/^UQ-[0-9]{8}-/, `UQ-${caseDate}-`)
      : qiCase.case_code;

    return {
      ...qiCase,
      case_code: correctedCode,
      session_datetime: firstSession.session_datetime,
      location: firstSession.location || qiCase.location,
    } as QiCase;
  });
}

export async function fetchCase(caseId: number): Promise<QiCase | null> {
  const { data, error } = await supabase
    .from('qi_cases')
    .select('*')
    .eq('id', caseId)
    .maybeSingle();
  if (error) throw error;
  return (data as QiCase) || null;
}

export async function fetchSessionRecords(caseId: number): Promise<QiSessionRecord[]> {
  const { data, error } = await supabase
    .from('qi_session_records')
    .select('*')
    .eq('case_id', caseId)
    .order('recorded_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw error;
  return (data as QiSessionRecord[]) || [];
}

export async function fetchRevisions(caseId: number): Promise<QiRevision[]> {
  const { data, error } = await supabase
    .from('qi_revisions')
    .select('*')
    .eq('case_id', caseId)
    .eq('entity_type', 'session_record')
    .order('changed_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw error;
  return (data as QiRevision[]) || [];
}

export async function fetchSessions(caseId: number): Promise<QiSession[]> {
  const { data, error } = await supabase
    .from('qi_sessions')
    .select('*')
    .eq('case_id', caseId)
    .order('session_number', { ascending: true });
  if (error) throw error;
  return (data as QiSession[]) || [];
}

export async function fetchNotifications(caseId: number): Promise<QiNotification[]> {
  const { data, error } = await supabase
    .from('qi_notifications')
    .select('*')
    .eq('case_id', caseId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as QiNotification[]) || [];
}

export async function fetchFollowups(caseId: number): Promise<QiFollowup[]> {
  const { data, error } = await supabase
    .from('qi_followups')
    .select('*')
    .eq('case_id', caseId)
    .order('due_date', { ascending: true });
  if (error) throw error;
  return (data as QiFollowup[]) || [];
}

export async function fetchAllFollowups(): Promise<QiFollowup[]> {
  const { data, error } = await supabase.from('qi_followups').select('*');
  if (error) throw error;
  return (data as QiFollowup[]) || [];
}

export async function fetchConsent(caseId: number): Promise<QiConsent | null> {
  const { data, error } = await supabase
    .from('qi_consents')
    .select('*')
    .eq('case_id', caseId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const row = data as QiConsent;
  // 受施者安全連結寫入 recipient_* 欄位；案例／Reviewer 頁一律以受施者的決定為準。
  return {
    ...row,
    storage_consent: row.recipient_storage_consent ?? row.storage_consent,
    followup_consent: row.recipient_followup_consent ?? row.followup_consent,
    internal_anonymous_use: row.recipient_internal_use ?? row.internal_anonymous_use,
    public_anonymous_use: row.recipient_public_use ?? row.public_anonymous_use,
    media_public_use: row.recipient_media_public_use ?? row.media_public_use,
    consented_at: row.recipient_consented_at ?? row.consented_at,
  };
}

export async function fetchMedia(caseId: number): Promise<QiMedia[]> {
  const { data, error } = await supabase
    .from('qi_media')
    .select('*')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as QiMedia[]) || [];
}

export async function fetchReview(caseId: number): Promise<QiReview | null> {
  const { data, error } = await supabase
    .from('qi_reviews')
    .select('*')
    .eq('case_id', caseId)
    .maybeSingle();
  if (error) throw error;
  return (data as QiReview) || null;
}

export interface PublicWitness {
  id: number;
  case_code: string | null;
  title: string | null;
  public_summary: string;
  evidence_level: string | null;
  review_status: string;
  published_at: string | null;
  session_datetime: string | null;
  location: string | null;
  sessions: Array<Record<string, unknown>>;
  records: Array<Record<string, unknown>>;
  followups: Array<Record<string, unknown>>;
  media: Array<Record<string, unknown>>;
}

export async function fetchWitnesses(): Promise<PublicWitness[]> {
  // RPC 僅回傳已發布案例的去識別完整內容；姓名、Email、電話、user id 與內部備註不會離開資料庫。
  const { data, error } = await supabase.rpc('get_public_witnesses');
  if (error) throw error;
  return (data as PublicWitness[]) || [];
}

export async function getPublicWitnessMediaUrl(path: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage.from('private').download(path);
    if (error || !data) return null;
    return URL.createObjectURL(data);
  } catch {
    return null;
  }
}

export async function fetchLatestDraft(userId: string): Promise<{ id: number; form_draft: QiCaseForm | null } | null> {
  // 以建立者（case_owner_id）為主要依據，並相容施炁者身分建立的舊資料。
  // 案主模式下 practitioner_id 是「選中的施炁者」，若只查 practitioner_id 會撈不到案主自己的草稿。
  const { data, error } = await supabase
    .from('qi_cases')
    .select('id, form_draft')
    .or(`case_owner_id.eq.${userId},practitioner_id.eq.${userId}`)
    .eq('status', 'draft')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { id: data.id, form_draft: (data.form_draft as QiCaseForm) || null };
}

// ── Persistence ─────────────────────────────────────

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function sanitizeFile(file: File): string {
  const raw = file.name.replace(/\.[^.]+$/, '');
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const safe = raw
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return `${safe || 'file'}.${ext}`;
}

export interface MediaUploadOptions {
  sessionId?: number | null;
  evidenceStage?: EvidenceStage | null;
  description?: string;
  containsFace?: boolean;
  identifyingInformation?: string;
  internalUseAllowed?: boolean;
  publicUseAllowed?: boolean;
}

export async function uploadQiMedia(
  file: File,
  caseId: number,
  stage: string,
  userId: string,
  publicPermission: boolean,
  options: MediaUploadOptions = {},
): Promise<QiMedia | null> {
  const safeName = sanitizeFile(file);
  const path = `qi/${caseId}/${Date.now()}-${safeName}`;
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const isAac = ['aac', 'acc', 'adts'].includes(extension)
    || file.type === 'audio/vnd.dlna.adts'
    || file.type === 'audio/aacp';
  // Windows/Chrome 常把手機錄音 AAC 回報為 audio/vnd.dlna.adts；統一為
  // 瀏覽器播放器較容易辨識的 audio/aac，並保留其他檔案原始 MIME。
  const knownContentTypes: Record<string, string> = {
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    m4a: 'audio/mp4',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    flac: 'audio/flac',
    opus: 'audio/ogg',
  };
  const contentType = isAac
    ? 'audio/aac'
    : (file.type || knownContentTypes[extension] || 'application/octet-stream');
  const { error: uploadErr } = await supabase.storage.from('private').upload(path, file, {
    upsert: false,
    contentType,
  });
  if (uploadErr) throw uploadErr;

  const audioExtensions = new Set(['aac', 'acc', 'adts', 'm4a', 'mp3', 'wav', 'ogg', 'oga', 'flac', 'opus']);
  const fileType = file.type.startsWith('video')
    ? 'video'
    : file.type.startsWith('image')
      ? 'image'
      : (file.type.startsWith('audio') || audioExtensions.has(extension))
        ? 'audio'
        : 'document';
  const { data, error } = await supabase
    .from('qi_media')
    .insert({
      case_id: caseId,
      session_id: options.sessionId ?? null,
      stage,
      evidence_stage: options.evidenceStage ?? null,
      file_path: path,
      file_type: fileType,
      public_permission: publicPermission,
      uploaded_by: userId,
      description: options.description ?? null,
      contains_face: options.containsFace ?? false,
      identifying_information: options.identifyingInformation ?? null,
      internal_use_allowed: options.internalUseAllowed ?? false,
      public_use_allowed: options.publicUseAllowed ?? false,
    })
    .select()
    .single();
  if (error) {
    await supabase.storage.from('private').remove([path]);
    throw error;
  }
  return (data as QiMedia) || null;
}

export async function setMediaPermission(
  mediaId: number,
  patch: {
    contains_face?: boolean;
    identifying_information?: string | null;
    internal_use_allowed?: boolean;
    public_use_allowed?: boolean;
    permission_withdrawn_at?: string | null;
    description?: string | null;
    evidence_stage?: EvidenceStage | null;
  },
): Promise<void> {
  const { error } = await supabase.from('qi_media').update(patch).eq('id', mediaId);
  if (error) throw error;
}

export async function deleteQiMedia(mediaId: number, reason = '使用者刪除錯誤或不需要的附件'): Promise<void> {
  const { data, error } = await supabase.rpc('delete_qi_media', {
    p_media_id: mediaId,
    p_reason: reason,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || '刪除附件失敗');
}

export async function updateQiCaseFields(
  caseId: number,
  changes: Record<string, unknown>,
  reason: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('update_qi_case_fields', {
    p_case_id: caseId,
    p_changes: changes,
    p_reason: reason,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || '更新案例資料失敗');
}

export async function addQiCaseAddendum(caseId: number, narrative: string): Promise<void> {
  const { data, error } = await supabase.rpc('add_qi_case_addendum', {
    p_case_id: caseId,
    p_narrative: narrative,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || '新增補充失敗');
}

export async function updateQiSession(
  sessionId: number,
  changes: Record<string, unknown>,
  reason: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('update_qi_session', {
    p_session_id: sessionId,
    p_changes: changes,
    p_reason: reason,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || '更新施炁資料失敗');
}

export async function getSignedMediaUrl(path: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage.from('private').createSignedUrl(path, 3600);
    if (error) {
      console.error('createSignedUrl error:', error);
    }
    if (data?.signedUrl) return data.signedUrl;
  } catch (err) {
    console.error('createSignedUrl exception:', err);
  }
  return null;
}

async function upsertSessionRecord(
  caseId: number,
  sessionId: number | null,
  stage: string,
  sourceType: string | null,
  structuredData: Record<string, unknown>,
  narrative: string | null,
  userId: string,
): Promise<void> {
  const { data: existing } = await supabase
    .from('qi_session_records')
    .select('id')
    .eq('case_id', caseId)
    .eq('record_stage', stage)
    .eq('session_id', sessionId ?? -1)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('qi_session_records')
      .update({ structured_data: structuredData, narrative, source_type: sourceType })
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('qi_session_records').insert({
      case_id: caseId,
      session_id: sessionId,
      record_stage: stage,
      source_type: sourceType,
      structured_data: structuredData,
      narrative,
      created_by: userId,
    });
    if (error) throw error;
  }
}

async function upsertConsent(caseId: number, form: QiCaseForm): Promise<void> {
  // 保存本案例與審核人員查看是建立案例的基本主流程，預設成立；
  // 只有「額外用途」（追蹤聯絡／內部研究／匿名公開／媒體公開）才由使用者主動勾選。
  const payload = {
    consent_version: 'v1.0',
    storage_consent: true,
    reviewer_access: true,
    followup_consent: form.followup_consent,
    internal_anonymous_use: form.internal_anonymous_use,
    public_anonymous_use: form.public_anonymous_use,
    media_public_use: form.media_public_use,
  };

  const { data: existing } = await supabase
    .from('qi_consents')
    .select('id')
    .eq('case_id', caseId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase.from('qi_consents').update(payload).eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('qi_consents').insert({ case_id: caseId, ...payload });
    if (error) throw error;
  }
}

async function ensureFollowups(caseId: number, sessionId: number | null, sessionDate: string): Promise<void> {
  const { data: existing } = await supabase
    .from('qi_followups')
    .select('followup_type')
    .eq('case_id', caseId)
    .eq('session_id', sessionId ?? -1);

  const existingTypes = new Set((existing || []).map((f) => f.followup_type));
  const base = sessionDate ? new Date(sessionDate) : new Date();

  const tasks: { followup_type: string; due_date: string }[] = [];
  if (!existingTypes.has('day7')) {
    tasks.push({ followup_type: 'day7', due_date: new Date(base.getTime() + 7 * 86400000).toISOString().slice(0, 10) });
  }
  if (!existingTypes.has('day30')) {
    tasks.push({ followup_type: 'day30', due_date: new Date(base.getTime() + 30 * 86400000).toISOString().slice(0, 10) });
  }
  if (!existingTypes.has('day90')) {
    tasks.push({ followup_type: 'day90', due_date: new Date(base.getTime() + 90 * 86400000).toISOString().slice(0, 10) });
  }

  if (tasks.length > 0) {
    const rows = tasks.map((t) => ({ case_id: caseId, session_id: sessionId, followup_type: t.followup_type, due_date: t.due_date, status: 'scheduled' }));
    const { error } = await supabase.from('qi_followups').insert(rows);
    if (error) throw error;
  }
}

export async function persistCase(
  form: QiCaseForm,
  userId: string,
  existingId: number | null,
  submit: boolean,
  isCaseOwner = false,
  isCandidate = false,
): Promise<QiCase | null> {
  let caseId = existingId;
  const sessionDateTime = normalizeSessionDateTime(form.session_datetime);
  let rawToken: string | null = null;

  // 施炁者身分：案主建案例時 practitioner_user_id 為選中的施炁者（或空=unverified）
  const practitionerId = form.practitioner_user_id || userId;
  // candidate（準施炁者）不得自稱正式授權施炁者 → 一律標記為未核對
  const practitionerVerified = isCandidate ? false : form.practitioner_verified;
  const practitionerNameSnapshot = !practitionerVerified ? form.practitioner_name || null : null;

  // 安全 token：只在「首次建立」或「正式送出」時產生；資料庫只存 sha256 hash。
  const isNew = !caseId;
  if (isNew || submit) rawToken = crypto.randomUUID();

  const casePayload = {
    case_owner_id: userId,
    practitioner_id: practitionerId,
    practitioner_name_snapshot: practitionerNameSnapshot,
    practitioner_verified: practitionerVerified,
    // candidate 建立的案例一律私人保存，不進入公開流程
    public_visibility: 'private',
    case_series_id: form.case_series_id || null,
    complaint_source: form.complaint_source || 'entered',
    recipient_id: isCaseOwner ? userId : null,
    recipient_alias: form.recipient_alias || null,
    recipient_contact: form.recipient_contact || null,
    recipient_email: form.recipient_email || null,
    session_datetime: sessionDateTime,
    location: form.location || null,
    is_first_session: form.is_first_session,
    referrer_user_id: form.referrer_user_id || null,
    referrer_name_snapshot: form.referrer_name || null,
    referrer_type: form.referrer_type || null,
    referral_code: form.referral_code || null,
    referral_source_text: form.referral_source_text || null,
    referred_at: form.referrer_type ? new Date().toISOString() : null,
    status: submit ? 'submitted' : 'draft',
    form_draft: form,
    updated_at: new Date().toISOString(),
  };

  if (!caseId) {
    const tokenHash = rawToken ? await sha256Hex(rawToken) : null;
    const { data, error } = await supabase
      .from('qi_cases')
      .insert({
        ...casePayload,
        recipient_token_hash: tokenHash,
        recipient_token_expires_at: new Date(Date.now() + 30 * 86400000).toISOString(),
      })
      .select()
      .single();
    if (error) throw error;
    caseId = (data as QiCase).id;
  } else if (submit && rawToken) {
    const tokenHash = await sha256Hex(rawToken);
    const { error } = await supabase.from('qi_cases').update({
      ...casePayload,
      recipient_token_hash: tokenHash,
      recipient_token: null,
      recipient_token_revoked_at: null,
      recipient_token_expires_at: new Date(Date.now() + 30 * 86400000).toISOString(),
    }).eq('id', caseId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('qi_cases').update(casePayload).eq('id', caseId);
    if (error) throw error;
  }

  // 確保第一次施炁的 session 存在（session_number = 1）
  let sessionId: number | null = null;
  if (caseId) {
    const { data: firstSession } = await supabase
      .from('qi_sessions')
      .select('id')
      .eq('case_id', caseId)
      .order('session_number', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (firstSession) {
      sessionId = (firstSession as { id: number }).id;
            // 草稿建立後仍可能修改實際施炁時間；第一次 session 才是追蹤排程的權威來源。
      // 同步 session，資料庫 trigger 會連動重算尚未完成的 7／30／90 日追蹤日期。
      const { error: sessionSyncError } = await supabase
        .from('qi_sessions')
        .update({
          session_datetime: sessionDateTime,
          location: form.location || null,
          method: form.method_contact || null,
        })
        .eq('id', sessionId);
      if (sessionSyncError) throw sessionSyncError;
    } else {
      const { data: ns, error: nsErr } = await supabase
        .from('qi_sessions')
        .insert({
          case_id: caseId,
          session_number: 1,
          session_datetime: sessionDateTime,
          location: form.location || null,
          method: form.method_contact || null,
          created_by: userId,
        })
        .select('id')
        .single();
      if (nsErr) throw nsErr;
      sessionId = (ns as { id: number })?.id ?? null;
    }
  }

  // Session records (stages 2–5)
  await upsertSessionRecord(
    caseId,
    sessionId,
    'pre_condition',
    form.pre_source_type || null,
    {
      main_issue: form.pre_main_issue,
      body_parts: form.pre_body_parts,
      discomfort: form.pre_discomfort,
      emotion: form.pre_emotion,
      function: form.pre_function,
      history: form.pre_history,
      treatment: form.pre_treatment,
    },
    form.pre_note || null,
    userId,
  );

  await upsertSessionRecord(
    caseId,
    sessionId,
    'method',
    'practitioner_observation',
    {
      start: form.method_start,
      end: form.method_end,
      contact: form.method_contact,
      position: form.method_position,
      audio: form.method_audio,
      stopped: form.method_stopped,
      attention: form.method_attention,
    },
    form.method_observation || null,
    userId,
  );

  await upsertSessionRecord(
    caseId,
    sessionId,
    'immediate_reaction',
    null,
    {
      reactions: form.reactions,
      other: form.reaction_other,
      start: form.reaction_start,
      duration: form.reaction_duration,
      recipient_note: form.reaction_recipient_note,
    },
    form.reaction_practitioner_note || null,
    userId,
  );

  await upsertSessionRecord(
    caseId,
    sessionId,
    'post_condition',
    null,
    {
      discomfort: form.post_discomfort,
      emotion: form.post_emotion,
      function: form.post_function,
      recipient_change: form.post_recipient_change,
      new_discomfort: form.post_new_discomfort,
      need_help: form.post_need_help,
      next_step: form.post_next_step,
      followup_24h: form.post_24h_followup,
    },
    form.post_practitioner_change || null,
    userId,
  );

  // Consent (stage 6)
  await upsertConsent(caseId, form);

  // Follow-up tasks on submit
  if (submit) {
    await ensureFollowups(caseId, sessionId, sessionDateTime);
  }

  const result = await fetchCase(caseId);
  // 資料庫只存 hash，此處把「本次產生的原始 token」暫存回回傳物件，供成功頁建立安全連結。
  if (result && rawToken) result.recipient_token = rawToken;
  return result;
}

// ── Review actions ──────────────────────────────────

export async function saveReview(
  caseId: number,
  reviewerId: string,
  input: { review_status: string; evidence_level: string; public_summary: string; internal_notes: string },
): Promise<void> {
  const { data: existing } = await supabase
    .from('qi_reviews')
    .select('id')
    .eq('case_id', caseId)
    .maybeSingle();

  const payload = {
    reviewer_id: reviewerId,
    review_status: input.review_status,
    evidence_level: input.evidence_level,
    public_summary: input.public_summary,
    internal_notes: input.internal_notes,
    reviewed_at: new Date().toISOString(),
  };

  if (existing) {
    const { error } = await supabase.from('qi_reviews').update(payload).eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('qi_reviews').insert({ case_id: caseId, ...payload });
    if (error) throw error;
  }

  // Update case evidence_level + status
  const statusByReview: Record<string, string> = {
    in_review: 'under_review',
    approved: 'approved_anonymous',
    need_more_evidence: 'need_more_evidence',
    rejected: 'private_complete',
  };
  const newStatus = statusByReview[input.review_status] || 'under_review';
  const { error } = await supabase
    .from('qi_cases')
    .update({ evidence_level: input.evidence_level, status: newStatus })
    .eq('id', caseId);
  if (error) throw error;
}

export async function publishWitness(
  caseId: number,
  title: string,
  publicSummary: string,
  evidenceLevel: string,
  requestSupplement = false,
): Promise<{ ok: boolean; action?: 'submit_first_review' | 'publish'; case_id?: number; noticeWarning?: string }> {
  // 首案由 ABAA 核准；同一主訴後續案例可由該施炁者發布。
  // 匿名文字由施炁者確認去識別後送審；露臉媒體仍須案主逐檔同意。
  const { data, error } = await supabase.rpc('publish_qi_witness', {
    p_case_id: caseId,
    p_title: title,
    p_public_summary: publicSummary,
    p_evidence_level: evidenceLevel,
  });
  if (error) {
    const raw = error.message || '';
    let msg = '後端拒絕發布：確認或發布資格未通過。';
    if (raw.includes('CONSENT_MISSING')) msg = '尚未取得受施者同意紀錄，後端已拒絕發布。';
    else if (raw.includes('CONSENT_INVALID')) msg = '受施者授權不足，後端已拒絕發布。';
    else if (raw.includes('PUBLISH_FORBIDDEN')) msg = '首次案例須由ABAA核准；後續案例僅限指定施炁者發布。';
    else if (raw.includes('PRACTITIONER_CONFIRMATION_REQUIRED')) msg = '請先由施炁者確認匿名內容並送審。';
    throw new Error(msg);
  }
  const result = (data || { ok: true }) as { ok: boolean; action?: 'submit_first_review' | 'publish'; case_id?: number };
  if (result.action === 'publish') {
    const warnings: string[] = [];
    if (!requestSupplement) try {
      const notice = await supabase.rpc('queue_qi_witness_published_notice', { p_case_id: caseId });
      if (notice.error) throw notice.error;
      const queued = notice.data as {
        queued?: boolean; notification_id?: number; recipient_email?: string; recipient_alias?: string;
      } | null;
      if (queued?.queued && queued.notification_id && queued.recipient_email) {
        const { error: emailError } = await supabase.functions.invoke('send_qi_email', {
          body: {
            notification_id: queued.notification_id,
            recipient_email: queued.recipient_email,
            recipient_alias: queued.recipient_alias,
            secure_url: `${window.location.origin}/og/qi-healing/witnesses`,
            notification_type: 'witness_published',
          },
        });
        if (emailError) throw emailError;
      }
    } catch {
      warnings.push('見證已發布，但案主的發布通知寄送失敗');
    }

    if (requestSupplement) {
      try {
        const queuedResult = await supabase.rpc('queue_qi_supplement_notices', { p_case_id: caseId });
        if (queuedResult.error) throw queuedResult.error;
        const notices = ((queuedResult.data as { notices?: Array<Record<string, unknown>> } | null)?.notices || []);
        const sends = notices.map((notice) => supabase.functions.invoke('send_qi_email', {
          body: {
            notification_id: notice.notification_id,
            recipient_email: notice.recipient_email,
            recipient_alias: notice.recipient_alias,
            recipient_kind: notice.recipient_kind,
            secure_url: notice.recipient_kind === 'practitioner'
              ? `${window.location.origin}/og/qi-healing/case/${caseId}`
              : `${window.location.origin}/og/qi-healing/witnesses`,
            notification_type: 'supplement_requested',
          },
        }));
        const settled = await Promise.allSettled(sends);
        if (settled.some((item) => item.status === 'rejected' || item.value.error)) {
          warnings.push('補件邀請有部分未寄出');
        }
      } catch {
        warnings.push('見證已發布，但補件邀請未寄出');
      }
    }
    if (warnings.length) return { ...result, noticeWarning: warnings.join('；') };
  }
  return result;
}

// ── Role management (admin) ────────────────────────

export interface QiUserRow {
  user_id: string;
  email: string | null;
  role: string | null;
  legal_name: string | null;
  qualification_status: string | null;
  practice_authorization_status: string | null;
  practitioner_status: string | null;
  observation_status: string | null;
  sacred_light_status: string | null;
  subfield_status: string | null;
  approved_at: string | null;
  email_confirmed_at: string | null;
  invited_at: string | null;
  last_sign_in_at: string | null;
  created_at: string;
}

export async function listQiUsers(): Promise<QiUserRow[]> {
  const { data, error } = await supabase.rpc('list_qi_admin_users_v2');
  if (error) throw error;
  return (data as QiUserRow[]) || [];
}

export async function setPractitionerStatus(userId: string, status: string): Promise<void> {
  const { error } = await supabase.rpc('set_practitioner_status', {
    p_user_id: userId,
    p_status: status,
  });
  if (error) {
    const raw = error.message || '';
    const msg = raw.includes('ROLE_FORBIDDEN')
      ? '僅系統管理員可設定施炁者培育狀態。'
      : raw.includes('INVALID_STATUS')
        ? '無效的培育狀態。'
        : '設定失敗，請稍後再試。';
    throw new Error(msg);
  }
}

export async function inviteQiPractitioner(
  email: string,
  practitionerStatus: 'candidate' | 'authorized',
): Promise<{ email: string; practitioner_status: string; activation_status: string }> {
  const { data, error } = await supabase.functions.invoke('qi_admin_invite', {
    body: {
      email: email.trim().toLowerCase(),
      practitioner_status: practitionerStatus,
      redirect_to: `${window.location.origin}/account?mode=invite`,
    },
  });

  if (error) throw new Error(error.message || '邀請失敗，請稍後再試。');
  if (!data?.success) {
    const reason = String(data?.reason || '');
    if (reason === 'EMAIL_ALREADY_ACTIVE') throw new Error('此 Email 已完成啟用，請直接在下方調整其施炁者資格。');
    if (reason === 'EMAIL_ALREADY_INVITED') throw new Error('此 Email 已受邀但尚未啟用，請提醒本人使用最新邀請信完成驗證。');
    if (reason === 'ROLE_FORBIDDEN') throw new Error('僅 OG ADM 可以邀請施炁者。');
    if (reason === 'INVALID_EMAIL') throw new Error('Email 格式不正確。');
    throw new Error(String(data?.message || '邀請失敗，請稍後再試。'));
  }
  return data;
}

export interface VerifiedPractitioner {
  user_id: string;
  legal_name: string | null;
  public_display_name?: string | null;
  practice_locations?: string[];
}

export async function listVerifiedPractitioners(location?: string): Promise<VerifiedPractitioner[]> {
  const { data, error } = await supabase.rpc('list_public_practitioners', {
    p_location: location || null,
  });
  if (error) {
    // Migration rollout compatibility: keep the existing directory available.
    const fallback = await supabase.rpc('list_verified_practitioners');
    if (fallback.error) throw fallback.error;
    return (fallback.data as VerifiedPractitioner[]) || [];
  }
  return ((data || []) as Array<{ user_id: string; public_display_name: string; practice_locations: string[] }>).map((item) => ({
    ...item,
    legal_name: null,
  }));
}

export async function getMyPractitionerProfile(): Promise<QiPractitionerProfile | null> {
  const { data, error } = await supabase.rpc('get_my_practitioner_profile');
  if (error) throw error;
  return (data as QiPractitionerProfile) || null;
}

export async function setPractitionerLegalName(userId: string, name: string): Promise<void> {
  const { error } = await supabase.rpc('set_practitioner_legal_name', {
    p_user_id: userId,
    p_name: name,
  });
  if (error) {
    const raw = error.message || '';
    const msg = raw.includes('ROLE_FORBIDDEN') ? '僅本人或管理員可設定實名。' : '設定失敗，請稍後再試。';
    throw new Error(msg);
  }
}

export async function grantPractitioner(userId: string): Promise<void> {
  const { error } = await supabase.rpc('grant_practitioner', { p_user_id: userId });
  if (error) {
    const raw = error.message || '';
    const msg = raw.includes('ROLE_FORBIDDEN') ? '僅系統管理員可授權施炁者。' : '授權失敗，請稍後再試。';
    throw new Error(msg);
  }
}

export async function revokePractitioner(userId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_practitioner', { p_user_id: userId });
  if (error) {
    const raw = error.message || '';
    const msg = raw.includes('ROLE_FORBIDDEN') ? '僅系統管理員可撤銷施炁者。' : '撤銷失敗，請稍後再試。';
    throw new Error(msg);
  }
}

// ── Session / Notification / Token / Dashboard ─────

export async function createSession(
  caseId: number,
  input: { session_datetime: string | null; location: string; method: string; practitioner_observation: string },
): Promise<{ session_id: number; session_number: number }> {
  const { data, error } = await supabase.rpc('create_qi_session', {
    p_case_id: caseId,
    p_session_datetime: normalizeSessionDateTime(input.session_datetime),
    p_location: input.location || null,
    p_method: input.method || null,
    p_practitioner_observation: input.practitioner_observation || null,
  });
  if (error) throw error;
  return data as { session_id: number; session_number: number };
}

export interface NotifyResult {
  recipient_identified: boolean;
  recipient_user_id?: string;
  reason?: string;
  message?: string;
  in_app_status?: string;
  email_status?: string;
  email_reason?: string;
  email_notification_id?: number;
}

export async function notifyRecipient(caseId: number, sendEmail = false): Promise<NotifyResult> {
  const { data, error } = await supabase.rpc('notify_recipient', { p_case_id: caseId, p_send_email: sendEmail });
  if (error) throw error;
  return data as NotifyResult;
}

export async function regenerateRecipientToken(caseId: number): Promise<{ token: string }> {
  const { data, error } = await supabase.rpc('regenerate_recipient_token', { p_case_id: caseId });
  if (error) throw error;
  return data as { token: string };
}

export async function revokeRecipientToken(caseId: number): Promise<{ revoked: boolean }> {
  const { data, error } = await supabase.rpc('revoke_recipient_token', { p_case_id: caseId });
  if (error) throw error;
  return data as { revoked: boolean };
}

export async function getPractitionerDashboard(): Promise<PractitionerDashboardRow[]> {
  const { data, error } = await supabase.rpc('get_practitioner_dashboard');
  if (error) throw error;
  return (data as PractitionerDashboardRow[]) || [];
}

export async function getMyNotifications(): Promise<MyNotificationRow[]> {
  const { data, error } = await supabase.rpc('get_my_notifications');
  if (error) throw error;
  return (data as MyNotificationRow[]) || [];
}

export async function bindRecipientByToken(token: string): Promise<{ bound: boolean; case_id: number }> {
  const { data, error } = await supabase.rpc('bind_recipient_by_token', { p_token: token });
  if (error) throw error;
  return data as { bound: boolean; case_id: number };
}

// ── Email 發送（Resend provider 抽象，走 Edge Function） ──

export interface SendEmailResult {
  email_status: string;
  reason?: string;
  detail?: string;
  resend_id?: string | null;
  http_status?: number | null;
  from?: string;
  domain_configured?: boolean;
  fetch_status?: number | null;
  message?: string;
}

export async function sendQiEmail(input: {
  notification_id?: number | null;
  recipient_email: string;
  recipient_alias?: string;
  practitioner_name?: string;
  secure_url: string;
}): Promise<SendEmailResult> {
  // 由 Supabase client 帶入目前 session 與專案設定，避免瀏覽器自行組合
  // Edge Function URL 時因環境值或快取差異而只留下 queued 紀錄。
  try {
    const { data, error } = await supabase.functions.invoke('send_qi_email', {
      body: {
        notification_id: input.notification_id ?? null,
        recipient_email: input.recipient_email,
        recipient_alias: input.recipient_alias,
        practitioner_name: input.practitioner_name,
        secure_url: input.secure_url,
      },
    });

    if (error) {
      return {
        email_status: 'failed',
        reason: 'edge_function_error',
        detail: error.message || 'Edge Function 呼叫失敗',
      };
    }

    const json = (data || {}) as Record<string, unknown>;
    return {
      email_status: (json.email_status as string) || 'unknown',
      reason: json.reason as string | undefined,
      detail: json.detail as string | undefined,
      resend_id: (json.resend_id as string | null) ?? null,
      http_status: (json.http_status as number | null) ?? null,
      from: json.from as string | undefined,
      domain_configured: json.domain_configured as boolean | undefined,
      fetch_status: 200,
    };
  } catch (err) {
    return {
      email_status: 'failed',
      reason: 'network_error',
      detail: err instanceof Error ? err.message : '網路連線失敗',
    };
  }
}

// ── Recipient edge-function client ─────────────────

// Cloudflare build variables can accidentally contain leading/trailing whitespace.
// Normalize here so a value such as "https://project.supabase.co " does not
// produce an invalid URL (".co /functions/...") and surface as Failed to fetch.
const QI_RECIPIENT_URL = `${String(import.meta.env.VITE_PUBLIC_SUPABASE_URL || '').trim().replace(/\/+$/, '')}/functions/v1/qi_recipient`;

export interface RecipientCaseInfo {
  id: number;
  case_code: string | null;
  recipient_alias: string | null;
  recipient_id: string | null;
  session_datetime: string | null;
  location: string | null;
  status: string;
  practitioner_name_snapshot: string | null;
  practitioner_verified: boolean;
  practitioner_qualification: string | null;
  referrer_type: string | null;
  referrer_name_snapshot: string | null;
  referral_code: string | null;
  recipient_confirmed_at?: string | null;
  practitioner_confirmed_at?: string | null;
}

export interface RecipientGetCaseResult {
  case: RecipientCaseInfo;
  sessions: QiSession[];
  records: QiSessionRecord[];
  media: QiMedia[];
  followups: QiFollowup[];
  consent: QiConsent | null;
}

async function callQiRecipient<T>(action: string, token: string, extra?: Record<string, unknown>): Promise<T> {
  const res = await fetch(QI_RECIPIENT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({ action, token, ...(extra || {}) }),
  });
  const json = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
  if (!res.ok) {
    const err = new Error(json.message || '連結無效或已失效') as Error & { code?: string };
    err.code = json.error;
    throw err;
  }
  return json as T;
}

export async function recipientGetCase(token: string): Promise<RecipientGetCaseResult> {
  return callQiRecipient('get_case', token);
}

export async function recipientUploadMedia(
  token: string,
  input: {
    file_data: string;
    file_name: string;
    file_type: string;
    evidence_stage?: string | null;
    description?: string | null;
    contains_face?: boolean;
    identifying_information?: string | null;
    session_id?: number | null;
  },
): Promise<{ success: boolean; media: QiMedia | null }> {
  return callQiRecipient('upload_media', token, input);
}

export async function recipientPrepareMediaUpload(
  token: string,
  input: { file_name: string; file_type: string; mime_type: string; file_size: number },
): Promise<{ upload_path: string; upload_token: string }> {
  return callQiRecipient('prepare_media_upload', token, input);
}

export async function uploadRecipientFileToSignedUrl(
  uploadPath: string,
  uploadToken: string,
  file: File,
): Promise<void> {
  const { error } = await supabase.storage.from('private').uploadToSignedUrl(uploadPath, uploadToken, file, {
    contentType: file.type || 'application/octet-stream',
  });
  if (error) throw error;
}

export async function recipientFinalizeMediaUpload(
  token: string,
  input: { upload_path: string; file_type: string; session_id?: number | null },
): Promise<{ success: boolean; media: (QiMedia & { signed_url?: string | null }) | null }> {
  return callQiRecipient('finalize_media_upload', token, input);
}

export async function recipientDeleteMedia(
  token: string,
  mediaId: number,
): Promise<{ success: boolean; deleted: boolean }> {
  return callQiRecipient('delete_media', token, { media_id: mediaId });
}

export async function recipientSetMediaPermission(
  token: string,
  mediaId: number,
  patch: {
    evidence_stage?: EvidenceStage | null;
    description?: string | null;
    contains_face?: boolean;
    identifying_information?: string | null;
    internal_use_allowed?: boolean;
    public_use_allowed?: boolean;
    permission_withdrawn_at?: string | null;
  },
): Promise<{ success: boolean }> {
  return callQiRecipient('set_media_permission', token, { media_id: mediaId, ...patch });
}

export async function recipientSubmitFirstPerson(
  token: string,
  report: { before: string; during: string; after: string; life_function: string; note: string },
): Promise<{ success: boolean; message: string }> {
  return callQiRecipient('submit_first_person', token, { report });
}

export async function recipientSubmitReport(
  token: string,
  report: Record<string, unknown>,
): Promise<{ success: boolean; message: string }> {
  return callQiRecipient('submit_report', token, { report });
}

export async function recipientSaveConsents(
  token: string,
  consents: RecipientConsentInput,
): Promise<{ success: boolean; message: string }> {
  return callQiRecipient('save_consents', token, { consents });
}

export async function recipientWithdrawPublicConsent(
  token: string,
): Promise<{ success: boolean; message: string }> {
  return callQiRecipient('withdraw_public_consent', token);
}

export async function recipientConfirmCase(
  token: string,
): Promise<{ success: boolean; message: string; confirmed_at: string }> {
  return callQiRecipient('confirm_case', token);
}

export async function confirmQiCaseByPractitioner(caseId: number): Promise<void> {
  const { error } = await supabase.rpc('confirm_qi_case_by_practitioner', { p_case_id: caseId });
  if (error) {
    const raw = error.message || '';
    if (raw.includes('ROLE_FORBIDDEN')) throw new Error('只有本案施炁者可以確認並送審。');
    throw error;
  }
}

// ── 案例治理（刪除／爭議申請） ──

// 將後端 RPC 拋出的錯誤碼轉成可讀訊息，並保留原始訊息作為最後防線。
function mapGovernanceError(error: { message?: string }): string {
  const raw = error.message || '';
  if (raw.includes('CASE_NOT_FOUND')) return '找不到此案例。';
  if (raw.includes('ROLE_FORBIDDEN')) return '你沒有權限執行此操作。';
  if (raw.includes('REASON_REQUIRED')) return '請填寫理由。';
  if (raw.includes('NOT_REQUESTED')) return '目前沒有可撤回的刪除申請。';
  return raw || '操作失敗，請稍後再試。';
}

export interface GovernanceResult {
  ok: boolean;
  error?: string;
}

// 申請刪除整個案例（施炁者／案主／Admin）。使用目前登入者 JWT，不套用 service role。
export async function requestQiCaseDeletion(
  caseId: number,
  reasonCategory: string,
  reason: string,
): Promise<GovernanceResult> {
  const { error } = await supabase.rpc('request_qi_case_deletion', {
    p_case_id: caseId,
    p_reason_category: reasonCategory,
    p_reason: reason,
  });
  if (error) return { ok: false, error: mapGovernanceError(error) };
  return { ok: true };
}

// 撤回尚未審核的刪除申請（僅申請人本人或 Admin）。
export async function withdrawQiCaseDeletion(caseId: number): Promise<GovernanceResult> {
  const { error } = await supabase.rpc('withdraw_qi_case_deletion', { p_case_id: caseId });
  if (error) return { ok: false, error: mapGovernanceError(error) };
  return { ok: true };
}

// 對案例提出紀錄爭議（施炁者／案主／Admin）。
export async function disputeQiCase(caseId: number, reason: string): Promise<GovernanceResult> {
  const { error } = await supabase.rpc('dispute_qi_case', { p_case_id: caseId, p_reason: reason });
  if (error) return { ok: false, error: mapGovernanceError(error) };
  return { ok: true };
}

// ── Admin 刪除審核 / 爭議處理 ──

// 將後端 Admin 審核 RPC 拋出的錯誤碼轉成可讀訊息，並保留原始訊息作為最後防線。
function mapAdminReviewError(error: { message?: string }): string {
  const raw = error.message || '';
  if (raw.includes('ADMIN_ONLY')) return '僅系統管理員可執行審核。';
  if (raw.includes('CASE_NOT_FOUND')) return '找不到此案例。';
  if (raw.includes('NOT_REQUESTED')) return '目前沒有待審核的刪除申請。';
  if (raw.includes('INVALID_ACTION')) return '審核動作無效。';
  if (raw.includes('REASON_REQUIRED')) return '請填寫審核備註。';
  if (raw.includes('CASE_CODE_MISMATCH')) return '案例編號不符，請重新載入後再試。';
  return raw || '操作失敗，請稍後再試。';
}

export interface AdminReviewResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

// 審核刪除申請（核准歸檔或駁回）。使用目前登入者 JWT，不套用 service role。
// p_action 僅 'archive'（核准歸檔）或 'reject'（駁回），絕不使用會物理刪除的 'approve_purge'。
export async function reviewQiCaseDeletion(
  caseId: number,
  action: 'archive' | 'reject',
  note: string,
  caseCode: string,
): Promise<AdminReviewResult> {
  const { data, error } = await supabase.rpc('review_qi_case_deletion', {
    p_case_id: caseId,
    p_action: action,
    p_note: note,
    p_case_code: caseCode,
  });
  if (error) return { ok: false, error: mapAdminReviewError(error) };
  return { ok: true, data };
}

// 標記紀錄爭議已解決。使用目前登入者 JWT，不套用 service role。
// 是否恢復公開完全依 RPC 既有規則（此 RPC 會一併清空 publication_paused_at）。
export async function resolveQiCaseDispute(
  caseId: number,
  resolution: string,
): Promise<AdminReviewResult> {
  const { data, error } = await supabase.rpc('resolve_qi_case_dispute', {
    p_case_id: caseId,
    p_resolution: resolution,
  });
  if (error) return { ok: false, error: mapAdminReviewError(error) };
  return { ok: true, data };
}


// ── 掛號主線與低摩擦匿名發布 ─────────────────────────────

export async function listMyQiCaseSeries() {
  const { data, error } = await supabase.rpc('list_my_qi_case_series');
  if (error) throw error;
  return data || [];
}

export async function createQiCaseSeries(input: {
  practitionerId: string | null;
  recipientAlias: string;
  chiefComplaint: string;
  locationCode: 'taipei' | 'hualien' | 'other';
}) {
  const { data, error } = await supabase.rpc('create_qi_case_series', {
    p_practitioner_id: input.practitionerId,
    p_recipient_alias: input.recipientAlias,
    p_chief_complaint: input.chiefComplaint,
    p_location_code: input.locationCode,
  });
  if (error) throw error;
  return data;
}

export async function submitQiCasePublication(caseId: number): Promise<'submit_first_review' | 'publish'> {
  const { data, error } = await supabase.rpc('submit_qi_case_publication', { p_case_id: caseId });
  if (error) throw error;
  return data as 'submit_first_review' | 'publish';
}

export async function approveQiSeriesFirstPublication(caseId: number, evidenceLevel?: string): Promise<void> {
  const { error } = await supabase.rpc('approve_qi_series_first_publication', {
    p_case_id: caseId,
    p_evidence_level: evidenceLevel || null,
  });
  if (error) throw error;
}

export async function pauseQiCasePublication(
  caseId: number,
  reasonCode: string,
  reasonOther?: string,
): Promise<void> {
  if (!reasonCode) throw new Error('撤下時必須選擇理由。');
  const { error } = await supabase.rpc('pause_qi_case_publication', {
    p_case_id: caseId,
    p_reason_code: reasonCode,
    p_reason_other: reasonOther?.trim() || null,
  });
  if (error) throw error;
}


export async function setPractitionerDirectory(input: {
  userId: string;
  publicDisplayName: string;
  practiceLocations: string[];
  directoryVisible: boolean;
}): Promise<void> {
  const { error } = await supabase.rpc('set_practitioner_directory', {
    p_user_id: input.userId,
    p_public_display_name: input.publicDisplayName,
    p_practice_locations: input.practiceLocations,
    p_directory_visible: input.directoryVisible,
  });
  if (error) throw error;
}
